import * as Sentry from "@sentry/react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import JoinPageAuthModal from "@/components/JoinPageAuthModal";
import { MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/classNames";
import {
  useJoinCampaignMutation,
  usePublicCampaignJoinInfoQuery,
  useUserCampaignsQuery,
} from "@/lib/queries/campaignQueries";

export default function CampaignJoinPage() {
  const { shortId } = useParams({ strict: false });
  const { code } = useSearch({ from: "/campaign/$shortId/join" });
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const joinMutation = useJoinCampaignMutation();
  const [joinError, setJoinError] = useState<string | null>(null);
  const [authJustCompleted, setAuthJustCompleted] = useState(false);

  const publicJoinInfoQuery = usePublicCampaignJoinInfoQuery(shortId ?? "");
  const campaignName = publicJoinInfoQuery.data?.campaignName ?? null;
  const selfJoinEnabled = publicJoinInfoQuery.data?.selfJoinEnabled ?? false;

  const userCampaignsQuery = useUserCampaignsQuery(undefined, Boolean(user));
  const alreadyMember = Boolean(
    shortId && userCampaignsQuery.data?.some((campaign) => campaign.shortId === shortId),
  );

  useEffect(() => {
    if (alreadyMember && shortId) {
      navigate({ to: "/campaign/$shortId", params: { shortId }, replace: true });
    }
  }, [alreadyMember, shortId, navigate]);

  const handleJoin = useCallback(async () => {
    if (!shortId || !code) return;
    setJoinError(null);
    try {
      await joinMutation.mutateAsync({ shortId, joinCode: code });
      navigate({ to: "/campaign/$shortId", params: { shortId }, replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to join campaign.";
      const isAlreadyMember = message.toLowerCase().includes("already a member");
      if (isAlreadyMember && shortId) {
        navigate({ to: "/campaign/$shortId", params: { shortId }, replace: true });
        return;
      }
      setJoinError(message);
      Sentry.captureException(error, { tags: { area: "campaign-join" } });
    }
  }, [shortId, code, joinMutation, navigate]);

  const handleAuthenticated = useCallback(() => {
    setAuthJustCompleted(true);
  }, []);

  if (authLoading || publicJoinInfoQuery.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f3eb]">
        <p className="text-sm uppercase tracking-[0.28em] text-neutral-600">Loading...</p>
      </div>
    );
  }

  if (publicJoinInfoQuery.isError || !campaignName) {
    return (
      <div className={cn("relative overflow-hidden", MONOLITH_BOARD_STYLE.shellClassName)}>
        <div className="flex min-h-screen items-center justify-center">
          <div className="max-w-md border-[3px] border-neutral-950 bg-[#fffaf1] p-8 shadow-[12px_12px_0_rgba(19,19,19,0.14)]">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-neutral-600">
              Campaign not found
            </p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-700">
              This invite link may be invalid or the campaign no longer exists.
            </p>
            <Button
              onClick={() => navigate({ to: "/", replace: true })}
              variant="primary"
              chrome="frame"
              type="button"
              className="mt-6"
            >
              Go home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!code) {
    return (
      <div className={cn("relative overflow-hidden", MONOLITH_BOARD_STYLE.shellClassName)}>
        <div className="flex min-h-screen items-center justify-center">
          <div className="max-w-md border-[3px] border-neutral-950 bg-[#fffaf1] p-8 shadow-[12px_12px_0_rgba(19,19,19,0.14)]">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-neutral-600">
              Missing join code
            </p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-700">
              This invite link is incomplete. Ask the campaign owner for a new link.
            </p>
            <Button
              onClick={() => navigate({ to: "/", replace: true })}
              variant="primary"
              chrome="frame"
              type="button"
              className="mt-6"
            >
              Go home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!user && !authJustCompleted) {
    return (
      <div className={cn("relative overflow-hidden", MONOLITH_BOARD_STYLE.shellClassName)}>
        <div className="flex min-h-screen items-center justify-center">
          <JoinPageAuthModal
            campaignName={campaignName}
            onAuthenticated={handleAuthenticated}
            onCancel={() => navigate({ to: "/", replace: true })}
          />
        </div>
      </div>
    );
  }

  if (user && (userCampaignsQuery.isPending || alreadyMember)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f3eb]">
        <p className="text-sm uppercase tracking-[0.28em] text-neutral-600">Loading...</p>
      </div>
    );
  }

  if (!selfJoinEnabled) {
    return (
      <div className={cn("relative overflow-hidden", MONOLITH_BOARD_STYLE.shellClassName)}>
        <div className="flex min-h-screen items-center justify-center">
          <div className="max-w-md border-[3px] border-neutral-950 bg-[#fffaf1] p-8 shadow-[12px_12px_0_rgba(19,19,19,0.14)]">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-neutral-600">
              Joining disabled
            </p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-700">
              Self-join is currently disabled for <strong>{campaignName}</strong>. Contact the campaign owner.
            </p>
            <Button
              onClick={() => navigate({ to: "/", replace: true })}
              variant="primary"
              chrome="frame"
              type="button"
              className="mt-6"
            >
              Go home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative overflow-hidden", MONOLITH_BOARD_STYLE.shellClassName)}>
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md border-[3px] border-neutral-950 bg-[#fffaf1] p-8 shadow-[12px_12px_0_rgba(19,19,19,0.14)]">
          <p className="text-[10px] uppercase tracking-[0.28em] text-neutral-600">Campaign Invite</p>
          <h1 className="mt-2 text-xl font-black uppercase tracking-[-0.04em] text-neutral-950">
            Join {campaignName}?
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-neutral-700">
            You've been invited to join this campaign as a player. Click below to accept.
          </p>

          {joinError ? (
            <div className="mt-4 border-[3px] border-[#7a1d16] bg-[#fff1ea] px-4 py-3 text-sm text-[#7a1d16]">
              {joinError}
            </div>
          ) : null}

          <div className="mt-8 flex flex-wrap items-center gap-3 border-t-2 border-neutral-950/12 pt-6">
            <Button
              onClick={() => navigate({ to: "/", replace: true })}
              disabled={joinMutation.isPending}
              variant="cancel"
              chrome="frame"
              type="button"
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleJoin()}
              disabled={joinMutation.isPending}
              variant="primary"
              chrome="frame"
              type="button"
            >
              {joinMutation.isPending ? "Joining\u2026" : "Join campaign"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
